/**
 * Single source of truth for all playable game examples and copyable templates.
 *
 * Every entry has: id, name, gameType, imageUrl.
 * Templates (isTemplate: true) also have campaignId for copy operations.
 * Items with showOnLanding: true appear in the landing page GamesGallery.
 * The full list is shown on the backend Game Templates page and AssetLibrary.
 *
 * To add/update a demo: edit this file. All consumers import from here.
 */

const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || '';

// ── Dev overrides ───────────────────────────────────────────────────────────
export const DEV_TEMPLATE_CAMPAIGN_ID =
  process.env.REACT_APP_DEV_TEMPLATE_CAMPAIGN_ID || null;

// ── Featured IDs ────────────────────────────────────────────────────────────
export const HERO_CAMPAIGN_ID = 'h0zOfUahCXNPQqL7ctmJ';
export const LEADERBOARD_EXAMPLE_ID =
  process.env.NODE_ENV === 'development' ? '0KsDLA9Rvj7KeHKRQaud' : 'h0zOfUahCXNPQqL7ctmJ';

// ── Unified game examples ───────────────────────────────────────────────────
// Order matters: templates first, then game-type examples in arcade order.
export const gameExamples = [
  // ── Brand templates (copyable) ──────────────────────────────────────────
  {
    id: 'stadium-sprint',
    name: 'Stadium Sprint',
    gameType: 'runner',
    gameTypeBadge: 'Lane Dash',
    campaignId: 'R83ciOeW2DjDdy9qv5dG',
    imageUrl: `${base}/GameTypeImages/stadiumSprint.jpg`,
    isTemplate: true,
    showOnLanding: false,
    // Template-specific
    copyName: 'Stadium Sprint',
    label: 'Product launch',
    useCase: 'Product launch',
    logoText: 'LAUNCH',
    accent: '#0ea5e9',
    accentGlow: 'rgba(14, 165, 233, 0.3)',
    bg: '#0c4a6e',
    bgSecondary: '#075985',
  },
  {
    id: 'mario-style',
    name: 'Mario Style',
    gameType: 'sidescroller',
    gameTypeBadge: 'Jump Runner',
    campaignId: 'drWfEwbP0CjNcaxMStjf',
    imageUrl: `${base}/GameTypeImages/marioStyle.jpg`,
    isTemplate: true,
    showOnLanding: false,
    copyName: 'Mario Style',
    label: 'Customise',
    useCase: 'Retro & fun',
    logoText: 'PLAY',
    accent: '#e11d48',
    accentGlow: 'rgba(225, 29, 72, 0.3)',
    bg: '#450a0a',
    bgSecondary: '#7f1d1d',
  },
  {
    id: 'pink-sky-bounce',
    name: 'Pink Sky Bounce',
    gameType: 'verticalclimber',
    gameTypeBadge: 'Sky Bounce',
    campaignId: 'zTYCgUCpnBGX7i5DSXiF',
    imageUrl: `${base}/GameTypeImages/pinkSkyBounce.jpg`,
    isTemplate: true,
    showOnLanding: false,
    copyName: 'Pink Sky Bounce',
    label: 'Events',
    useCase: 'Events & promos',
    logoText: 'BOUNCE',
    accent: '#ec4899',
    accentGlow: 'rgba(236, 72, 153, 0.3)',
    bg: '#831843',
    bgSecondary: '#9d174d',
  },

  // ── Game type examples ──────────────────────────────────────────────────
  // Arcade order: flappy, runner, sidescroller, verticalclimber, stacker, minigolf, snake
  {
    id: 'flappy-example',
    name: 'Flap & Fly',
    gameType: 'flappy',
    gameTypeBadge: 'Flap & Fly',
    imageUrl: `${base}/GameTypeImages/flappy.jpg`,
    description: 'Navigate through gaps in this addictive flyer',
    category: 'arcade',
    isTemplate: false,
    showOnLanding: true,
  },
  {
    id: 'runner-example',
    name: 'Lane Dash',
    gameType: 'runner',
    gameTypeBadge: 'Lane Dash',
    imageUrl: `${base}/GameTypeImages/lanedash.jpg`,
    description: 'Dodge obstacles in this fast-paced runner',
    tag: 'FAST',
    category: 'arcade',
    isTemplate: false,
    showOnLanding: true,
  },
  {
    id: 'sidescroller-example',
    name: 'Jump Runner',
    gameType: 'sidescroller',
    gameTypeBadge: 'Jump Runner',
    imageUrl: `${base}/GameTypeImages/jumprunner.jpg`,
    description: 'Time your jumps to survive as long as possible',
    tag: 'EVENTS',
    category: 'arcade',
    isTemplate: false,
    showOnLanding: true,
  },
  {
    id: 'verticalclimber-example',
    name: 'Sky Bounce',
    gameType: 'verticalclimber',
    gameTypeBadge: 'Sky Bounce',
    imageUrl: `${base}/GameTypeImages/skybounce.jpg`,
    description: 'Bounce higher and higher in this vertical climber',
    tag: 'HARD',
    category: 'arcade',
    isTemplate: false,
    showOnLanding: true,
  },
  {
    id: 'stacker-example',
    name: 'Stacker',
    gameType: 'stacker',
    gameTypeBadge: 'Stacker',
    imageUrl: `${base}/GameTypeImages/stacker.jpg`,
    description: 'Stack blocks with precision timing',
    category: 'arcade',
    isTemplate: false,
    showOnLanding: true,
  },
  {
    id: 'minigolf-example',
    name: 'Minigolf',
    gameType: 'minigolf',
    gameTypeBadge: 'Minigolf',
    imageUrl: `${base}/GameTypeImages/minigolf.jpg`,
    description: 'Aim and putt across themed courses',
    category: 'arcade',
    isTemplate: false,
    showOnLanding: true,
  },
  {
    id: 'snake-example',
    name: 'Snake',
    gameType: 'snake',
    gameTypeBadge: 'Snake',
    imageUrl: `${base}/GameTypeImages/snake.jpg`,
    description: 'Classic arcade snake with your branding',
    tag: 'CLASSIC',
    category: 'arcade',
    isTemplate: false,
    showOnLanding: true,
  },
  {
    id: 'quizrush-example',
    name: 'Quiz Rush',
    gameType: 'quizrush',
    gameTypeBadge: 'Quiz Rush',
    imageUrl: `${base}/GameTypeImages/quizImage.jpg`,
    description: 'Timed multiple-choice trivia with optional leaderboard',
    tag: 'TRIVIA',
    category: 'trivia',
    isTemplate: false,
    showOnLanding: true,
    isBonus: true,
  },
  {
    id: 'completelist-example',
    name: 'Complete the List',
    gameType: 'completelist',
    gameTypeBadge: 'Complete the List',
    imageUrl: `${base}/GameTypeImages/quizImage.jpg`,
    description: 'A fast list challenge for branded trivia and prize draws',
    tag: 'NEW',
    category: 'trivia',
    isTemplate: false,
    showOnLanding: true,
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Examples shown on the landing page GamesGallery (non-template, showOnLanding). */
export const getLandingExamples = () =>
  gameExamples.filter((e) => e.showOnLanding && !e.isTemplate);

/** Brand templates only (copyable). */
export const getTemplates = () =>
  gameExamples.filter((e) => e.isTemplate);

/** Find the first example for a given game type. */
export const getExampleByGameType = (gameType) =>
  gameExamples.find((e) => e.gameType === gameType && !e.isTemplate);

/** Resolve template campaign ID for copy operations, respecting dev override. */
export const getTemplateCampaignId = (example) =>
  DEV_TEMPLATE_CAMPAIGN_ID || example.campaignId;
